import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import type { LocalExercise } from '../../db/local.js';
import { listExercises } from '../../db/exercises.js';
import { progressOfExercise, trainingStreak, weeklyVolume } from '../../db/workouts.js';
import type { ExerciseProgress, WeekVolume } from '../../db/workouts.js';
import { formatNumber } from './SessionScreen.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { Sparkline } from '../Sparkline.js';
import { formatDay } from '../../format.js';

/**
 * Fortschritt: Serie, Wochenvolumen, geschätztes Maximum.
 *
 * Alle drei sind Schätzgrößen für den Vergleich mit sich selbst über Wochen —
 * kein Urteil. Deshalb steht hier ein Verlauf und keine Note.
 */
export function ProgressPanel(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timezone = useTimeZone() ?? 'Europe/Berlin';

  const streak = useLiveQuery(() => trainingStreak(timezone, db), [timezone], 0);
  const weeks = useLiveQuery(() => weeklyVolume(timezone, db, 6), [timezone], [] as WeekVolume[]);
  const exercises = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);

  const maxKg = Math.max(1, ...weeks.map((week) => week.summary.kg));

  return (
    <section aria-labelledby="progress-heading" className="flex flex-col gap-4">
      <h2 id="progress-heading" className="text-lg font-semibold">
        {t('training.progress.heading')}
      </h2>

      <div className="kw-tile">
        <p className="text-sm text-ink-muted">{t('training.progress.streak')}</p>
        <p className="text-2xl font-semibold">
          {t('training.progress.streakValue', { count: streak })}
        </p>
        <p className="mt-1 text-xs text-ink-muted">{t('training.progress.streakWhy')}</p>
      </div>

      <div className="kw-tile">
        <h3 className="text-sm font-medium text-ink-muted">
          {t('training.progress.weeklyVolume')}
        </h3>
        <ul className="mt-3 flex flex-col gap-2">
          {weeks.map((week) => (
            <li key={week.week} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-ink-muted">
                  {formatDay(week.week, 'date', i18n.language)}
                </span>
                <span>
                  {formatNumber(Math.round(week.summary.kg))} kg ·{' '}
                  {t('training.progress.sessions', { count: week.sessions })}
                </span>
              </div>
              <div
                className="h-2 rounded bg-surface-raised"
                role="img"
                aria-label={t('training.progress.barLabel', {
                  week: week.week,
                  kg: Math.round(week.summary.kg),
                })}
              >
                <div
                  className="h-2 rounded bg-accent"
                  style={{ width: `${Math.round((week.summary.kg / maxKg) * 100)}%` }}
                />
              </div>
              {week.summary.uncountedSets > 0 ? (
                // Eine Summe, die Läufe und Planken unterschlägt, sähe aus wie
                // eine vollständige Antwort.
                <p className="text-xs text-ink-muted">
                  {t('training.progress.uncounted', { count: week.summary.uncountedSets })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="kw-tile">
        <h3 className="text-sm font-medium text-ink-muted">{t('training.progress.oneRepMax')}</h3>
        <p className="mt-1 text-xs text-ink-muted">{t('training.progress.oneRepMaxWhy')}</p>
        <ul className="mt-3 flex flex-col gap-2">
          {exercises
            .filter((exercise) => exercise.isCompound)
            .map((exercise) => (
              <li key={exercise.id}>
                <ExerciseEstimate exercise={exercise} timeZone={timezone} />
              </li>
            ))}
        </ul>
      </div>
    </section>
  );
}

function ExerciseEstimate({
  exercise,
  timeZone,
}: {
  exercise: LocalExercise;
  timeZone: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const progress = useLiveQuery(
    () => progressOfExercise(exercise, db, timeZone),
    [exercise.id, timeZone],
    null,
  ) as ExerciseProgress | null;

  if (!progress || progress.totalSets === 0) return null;

  // Verlauf ab zwei Trainingstagen — die letzten zwölf, sonst wird die Linie zur Fläche.
  const recent = progress.history.slice(-12);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="min-w-0 truncate">{exercise.name}</span>
        <span className="shrink-0 text-ink-muted">
          {progress.bestOneRepMaxKg === null
            ? // Ausdrücklich: Ohne auswertbaren Satz gibt es keine Schätzung.
              t('training.progress.noEstimate')
            : t('training.progress.estimate', {
                best: formatNumber(Math.round(progress.bestOneRepMaxKg)),
                sets: progress.totalSets,
              })}
        </span>
      </div>
      {recent.length >= 2 ? (
        <Sparkline
          values={recent.map((point) => point.kg)}
          summary={t('training.progress.historySummary', {
            name: exercise.name,
            first: formatNumber(Math.round(recent[0]!.kg)),
            last: formatNumber(Math.round(recent.at(-1)!.kg)),
            days: recent.length,
          })}
        />
      ) : null}
    </div>
  );
}
